<script lang="ts">
  import {
    CATEGORY_META, categoryLabel, TIMINGS, TRACK_NAMES, TRACK_POINTS,
    type ErrorCode, type PlayerView, type PublicPlayer, type Track,
  } from '@banger/shared';
  import { betweenGoal, celebrationLine, celebrationWord, goalAway, lateJoinCopy, phoneTrackLabel, pointsLabel, winGot, winHeadline } from '../lib/trackCopy';
  import { clockOffset } from '../lib/view';
  import { secondsLeft } from '../lib/countdown';
  import { lastError } from '../lib/connection';
  import Avatar from '../shared/Avatar.svelte';
  import TimerRing from '../shared/TimerRing.svelte';
  import BangerFlash from '../shared/BangerFlash.svelte';
  import PatternGrid from '../shared/PatternGrid.svelte';
  import CardGrid from './CardGrid.svelte';
  import AnswerInput from './AnswerInput.svelte';
  import Result from './Result.svelte';

  let { view, onleave }: { view: PlayerView; onleave?: () => void } = $props();

  const me = $derived(view.me);
  const byId = $derived(new Map<string, PublicPlayer>(view.players.map((p) => [p.id, p])));
  const myPlayer = $derived(byId.get(me.id));
  const cat = $derived(view.category ? CATEGORY_META[view.category] : null);
  const decades = $derived([...view.settings.decades].sort((a, b) => a - b));
  const nameOf = (id: string) => byId.get(id)?.name ?? '?';

  const album = $derived(view.mode === 'album');
  const track = $derived(album ? view.track : null);
  // Track 2 only: the pattern stays set through Track 3, where the whole card is the goal.
  const outline = $derived(track === 2 && view.pattern ? view.pattern.cells : []);

  // One shared clock; a paused room has no deadline, so the last value is held.
  let now = $state(Date.now());
  $effect(() => {
    const t = setInterval(() => (now = Date.now()), 250);
    return () => clearInterval(t);
  });
  let secs = $state(0);
  $effect(() => {
    if (!view.paused) secs = secondsLeft(view.deadline, now, $clockOffset);
  });

  const lateJoiner = $derived(me.card === null && ['spin', 'answer', 'reveal', 'standings'].includes(view.phase));
  const sittingOut = $derived(view.tiebreak !== null && !view.tiebreak.includes(me.id));
  const tiedNames = $derived((view.tiebreak ?? []).map(nameOf).join(' vs '));

  // Album: simultaneous finishers all win the track and share the celebration.
  const winners = $derived(view.winners.flatMap((id) => byId.get(id) ?? []));
  const iWon = $derived(view.phase === 'banger' && view.winners.includes(me.id));
  const coWinners = $derived(winners.filter((p) => p.id !== me.id));

  // Vibrate once per BANGER!, not on every repeated view.
  let buzzedFor = '';
  $effect(() => {
    if (!iWon) return;
    const key = `${view.gameIndex}:${view.roundNumber}`;
    if (key === buzzedFor) return;
    buzzedFor = key;
    navigator.vibrate?.([200, 100, 200, 100, 400]);
  });

  // Steals involving me, announced at standings (or under the BANGER! when the round ends a game).
  const stealNews = $derived(
    view.events.flatMap((e) => {
      if (e.type !== 'steal') return [];
      if (e.by === me.id) return [`😈 You stole a square from ${nameOf(e.target)}!`];
      if (e.target === me.id) return [`😱 ${nameOf(e.by)} stole one of your squares!`];
      return [];
    }),
  );

  const closest = $derived(
    view.players.filter((p) => p.squaresToGoal !== null).sort((a, b) => a.squaresToGoal! - b.squaresToGoal!),
  );
  const place = $derived(view.ranking ? view.ranking.indexOf(me.id) + 1 : 0);

  // Album between: `view.track` is the track just won; ranked by points.
  const nextTrack = $derived(Math.min(3, (view.track ?? 1) + 1) as Exclude<Track, 1>);
  const ranked = $derived((view.ranking ?? []).flatMap((id) => byId.get(id) ?? []));
  const FULL_CARD = Array.from({ length: 25 }, (_, i) => i);
  function ordinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!);
  }

  // Errors show briefly as a toast. lastError is cleared by the next view, so latch it.
  const TOO_LATE = new Set<ErrorCode>(['NOT_ELIGIBLE', 'WRONG_PHASE', 'STALE_ROUND']);
  let toast = $state<string | null>(null);
  $effect(() => {
    const err = $lastError;
    if (!err) return;
    toast = TOO_LATE.has(err.code) ? 'Too late!' : err.code === 'ALREADY_ANSWERED' ? 'Already locked in' : 'Oops — try again';
    navigator.vibrate?.(60);
  });
  $effect(() => {
    if (!toast) return;
    const t = setTimeout(() => (toast = null), 2200);
    return () => clearTimeout(t);
  });
</script>

{#if view.phase === 'banger' && iWon && myPlayer}
  <BangerFlash
    emoji={myPlayer.emoji}
    name={myPlayer.name}
    others={coWinners}
    word={celebrationWord(track)}
    got={track === 2 ? celebrationLine([], track, true) : winGot(view)}
    caption={[track ? `That's you! 🎉 +${TRACK_POINTS[track]} pts` : "That's you! 🎉", ...stealNews].join('\n')}
  />
{:else}
  <main style="--c:{cat?.color ?? 'var(--pink)'}">
    <header>
      <span class="who">{#if myPlayer}<Avatar emoji={myPlayer.emoji} size={36} /> <b>{myPlayer.name}</b>{/if}</span>
      {#if album && myPlayer}
        <span class="game"><b class="pts">{pointsLabel(myPlayer.points ?? 0)}</b><small>{phoneTrackLabel(view)}</small></span>
      {:else}
        <span class="game">{phoneTrackLabel(view)}</span>
      {/if}
    </header>

    {#if lateJoiner}
      <section class="center">
        <h2 class="neon">You're in!</h2>
        <p class="lead">{lateJoinCopy(view)}</p>
        {#if cat}<p class="catname">{categoryLabel(view.category!, view.cutoffYear)}</p>{/if}
        <ol class="board">
          {#each closest as p (p.id)}
            <li><Avatar emoji={p.emoji} size={32} /><span class="nm">{p.name}</span><span>{p.squaresToGoal === 0 ? (track && track > 1 ? 'DONE!' : 'LINE!') : `${p.squaresToGoal} away`}</span></li>
          {/each}
        </ol>
      </section>
    {:else if view.phase === 'spin' && cat && me.card}
      <section class="stack">
        {#key view.roundNumber}<h2 class="catname spin">{categoryLabel(view.category!, view.cutoffYear)}</h2>{/key}
        {#if sittingOut}
          <p class="sitout">Tiebreak! {tiedNames} — sit this one out 🍿</p>
        {:else if view.tiebreak}
          <p class="tb">TIEBREAK — first correct answer wins!</p>
        {/if}
        <CardGrid card={me.card} marked={me.marked} cutoff={view.cutoffYear} {outline} />
      </section>
    {:else if view.phase === 'answer' && cat && view.category && me.card}
      <section class="stack">
        <div class="bar">
          <h2 class="catname small">{categoryLabel(view.category!, view.cutoffYear)}</h2>
          {#key view.roundNumber}<TimerRing {secs} total={TIMINGS.answerMs / 1000} size="60px" hurry={secs <= 5} />{/key}
        </div>
        {#if sittingOut}
          <p class="sitout">Tiebreak! {tiedNames} — sit this one out 🍿</p>
        {:else}
          {#if view.tiebreak}<p class="tb">TIEBREAK — first correct answer wins!</p>{/if}
          {#key view.roundNumber}
            <AnswerInput category={view.category} cutoff={view.cutoffYear} {decades} round={view.roundNumber} answer={me.answer} />
          {/key}
        {/if}
        <CardGrid card={me.card} marked={me.marked} cutoff={view.cutoffYear} {outline} />
      </section>
    {:else if view.phase === 'reveal' && view.category}
      {#key view.roundNumber}<Result {view} {secs} {sittingOut} {outline} />{/key}
    {:else if view.phase === 'standings' && me.card}
      <section class="stack">
        {#each stealNews as line}<p class="news">{line}</p>{/each}
        <p class="away neon">{goalAway(myPlayer?.squaresToGoal, view)}</p>
        <CardGrid card={me.card} marked={me.marked} fresh={me.markedThisRound} cutoff={view.cutoffYear} {outline} />
      </section>
    {:else if view.phase === 'banger'}
      <section class="center">
        <div class="avatars">{#each winners as w (w.id)}<Avatar emoji={w.emoji} size={winners.length > 1 ? 72 : 96} />{/each}</div>
        <h2 class="neon">{winHeadline(winners.map((w) => w.name), track)}</h2>
        {#if track}<p class="lead">+{TRACK_POINTS[track]} pts · Track {track} of 3</p>{/if}
        {#each stealNews as line}<p class="news">{line}</p>{/each}
      </section>
    {:else if view.phase === 'between'}
      <section class="center">
        <p class="kicker">Track {view.track} done! · Next up</p>
        <h2 class="track neon" class:platinum={nextTrack === 3}>Track {nextTrack} · {TRACK_NAMES[nextTrack]}</h2>
        {#if nextTrack === 3}
          <PatternGrid platinum cells={FULL_CARD} label="the whole card" size="7.5rem" />
        {:else if view.pattern}
          <PatternGrid cells={view.pattern.cells} label={view.pattern.label} size="7.5rem" />
          <p class="pname">{view.pattern.label}</p>
        {/if}
        <p class="goal">{betweenGoal(nextTrack)}</p>
        <p class="lead">Starting in {secs}s · your card carries over</p>
        <ol class="board">
          {#each ranked as p (p.id)}
            <li class:me={p.id === me.id}><Avatar emoji={p.emoji} size={32} /><span class="nm">{p.name}</span><span>{p.points ?? 0} {p.points === 1 ? 'pt' : 'pts'}</span></li>
          {/each}
        </ol>
        {#if onleave}<button class="leave" type="button" onclick={onleave}>Leave game</button>{/if}
      </section>
    {:else if view.phase === 'finale'}
      <section class="center">
        {#if place === 1}
          <p class="kicker">👑 {album ? 'ALBUM' : 'BANGER'} CHAMPION 👑</p>
          <h2 class="place neon">You won!</h2>
        {:else if place > 0}
          <h2 class="place neon">{ordinal(place)} place</h2>
          <p class="lead">of {view.ranking!.length}</p>
        {/if}
        {#if myPlayer}
          <div class="stats">
            {#if album}
              <div><b>{myPlayer.points ?? 0}</b><span>pts</span></div>
            {:else}
              <div><b>{myPlayer.bangers}</b><span>BANGER{myPlayer.bangers === 1 ? '' : 's'}</span></div>
            {/if}
            <div><b>{myPlayer.correctTotal}</b><span>correct</span></div>
          </div>
        {/if}
        <p class="wait">Waiting for the host…</p>
        {#if onleave}
          <div class="exits">
            <button class="btn-neon" type="button" onclick={onleave}>Join another game</button>
            <button class="leave" type="button" onclick={onleave}>Leave game</button>
          </div>
        {/if}
      </section>
    {/if}
  </main>
{/if}

{#if view.paused}
  <div class="paused" role="status">
    <span class="neon">{view.waitingForDisplay ? 'Waiting for the big screen…' : 'Paused by host'}</span>
    {#if view.waitingForDisplay && onleave}<button class="leave" type="button" onclick={onleave}>Leave game</button>{/if}
  </div>
{/if}

{#if toast}
  <div class="toast" role="alert">{toast}</div>
{/if}

<style>
  main {
    min-height: 100%; display: flex; flex-direction: column; gap: 14px; max-width: 480px; margin: 0 auto;
    padding: max(12px, env(safe-area-inset-top)) 16px max(20px, env(safe-area-inset-bottom));
  }
  header { display: flex; align-items: center; justify-content: space-between; gap: 8px; font-size: 0.95rem; }
  .who { display: inline-flex; align-items: center; gap: 8px; min-width: 0; }
  .who b { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .game { opacity: 0.7; white-space: nowrap; display: grid; justify-items: end; line-height: 1.15; }
  .game:has(.pts) { opacity: 1; }
  .game small { font-size: 0.8rem; opacity: 0.7; }
  .pts { font-size: 1.15rem; font-weight: 900; color: var(--cyan); text-shadow: 0 0 10px rgba(125, 249, 255, 0.6); }
  .stack { display: grid; gap: 14px; }
  .center { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; text-align: center; }
  h2 { margin: 0; font-size: 2rem; font-weight: 900; }
  .catname {
    margin: 0; text-align: center; font-size: 2.2rem; font-weight: 900; line-height: 1.1; color: var(--c);
    text-shadow: 0 0 10px var(--c), 0 0 28px color-mix(in srgb, var(--c) 60%, transparent);
  }
  .catname.spin { animation: land 0.6s cubic-bezier(0.2, 1.6, 0.4, 1) both; font-size: 2.6rem; padding: 12px 0; }
  .catname.small { font-size: 1.5rem; text-align: left; }
  .bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .tb { margin: 0; text-align: center; font-weight: 900; color: var(--cyan); text-shadow: 0 0 10px var(--cyan); }
  .sitout { margin: 0; text-align: center; font-size: 1.4rem; font-weight: 800; padding: 20px 12px; border-radius: 16px; background: rgba(255, 255, 255, 0.06); }
  .lead { margin: 0; font-size: 1.2rem; opacity: 0.9; }
  .news { margin: 0; text-align: center; font-size: 1.3rem; font-weight: 900; }
  .away { margin: 0; text-align: center; font-size: 1.8rem; font-weight: 900; }
  .board { list-style: none; margin: 0; padding: 0; width: 100%; display: grid; gap: 6px; }
  .board li { display: flex; align-items: center; gap: 10px; padding: 6px 14px 6px 6px; border-radius: 999px; background: rgba(255, 255, 255, 0.06); font-weight: 700; }
  .board li.me { border: 2px solid var(--cyan); }
  .nm { flex: 1; text-align: left; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .avatars { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; }
  .track { font-size: 1.8rem; line-height: 1.1; }
  .track.platinum { color: #fff; text-shadow: 0 0 10px #fff, 0 0 26px #b8c4d6, 0 0 50px #7df9ff; }
  .pname { margin: 0; font-size: 1.4rem; font-weight: 900; color: var(--cyan); text-shadow: 0 0 12px rgba(125, 249, 255, 0.7); }
  .goal { margin: 0; font-size: 1.15rem; font-weight: 800; max-width: 24ch; line-height: 1.25; }
  .kicker { margin: 0; letter-spacing: 0.15em; font-weight: 900; color: var(--cyan); }
  .place { font-size: 3rem; }
  .stats { display: flex; gap: 28px; }
  .stats div { display: grid; }
  .stats b { font-size: 2.6rem; color: var(--cyan); text-shadow: 0 0 12px var(--cyan); }
  .wait { margin: 0; opacity: 0.7; animation: breathe 2s ease-in-out infinite; }
  .exits { display: grid; gap: 10px; width: 100%; max-width: 320px; }
  .exits .btn-neon { min-height: 56px; font-size: 1.2rem; }
  .leave {
    min-height: 48px; padding: 0 24px; border-radius: 999px; font: inherit; font-weight: 800; color: #fff; cursor: pointer;
    background: transparent; border: 2px solid rgba(255, 255, 255, 0.35);
  }
  .paused {
    position: fixed; inset: 0; z-index: 60; display: flex; align-items: center; justify-content: center; text-align: center; padding: 16px;
    background: rgba(10, 6, 24, 0.8); backdrop-filter: blur(4px); font-size: 2.4rem; font-weight: 900;
    flex-direction: column; gap: 24px;
  }
  .paused .leave { font-size: 1.1rem; }
  .toast {
    position: fixed; left: 50%; bottom: max(24px, env(safe-area-inset-bottom)); transform: translateX(-50%); z-index: 70;
    padding: 12px 24px; max-width: calc(100vw - 24px); border-radius: 999px; font-weight: 900; font-size: 1.2rem; white-space: nowrap;
    background: #0a0618; border: 2px solid var(--pink); box-shadow: 0 0 18px var(--pink); animation: rise 0.25s ease-out both;
  }
  @keyframes land { from { transform: scale(0.4) rotate(-8deg); opacity: 0; } to { transform: none; opacity: 1; } }
  @keyframes rise { from { transform: translate(-50%, 20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }
  @keyframes breathe { 50% { opacity: 0.4; } }
</style>
